import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { commentsReplyLog } from "@/lib/social-comments.functions";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { Input } from "@/components/ui/input";
import { Lock, ScrollText, Bot, User, Sparkles, Pencil } from "lucide-react";

export const Route = createFileRoute("/admin/social/reply-log")({
  component: ReplyLogPage,
  ssr: false,
});

interface LogEntry {
  id: string;
  platform: string | null;
  account_id: string | null;
  author_name: string | null;
  author_handle: string | null;
  message: string | null;
  ai_suggested_reply: string | null;
  replied_message: string | null;
  replied_at: string | null;
  replied_by: string | null;
  replied_by_name: string;
  comment_at: string | null;
  used_ai: boolean;
  edited: boolean;
  status: string | null;
}

function fmt(d: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleString();
}

function ReplyLogPage() {
  const { isAdmin, loading } = useAdminAuth();
  const logFn = useServerFn(commentsReplyLog);
  const [search, setSearch] = useState("");

  const logQ = useQuery({
    queryKey: ["social-reply-log"],
    queryFn: () => logFn(),
    enabled: isAdmin,
  });

  const entries = (logQ.data?.entries ?? []) as LogEntry[];
  const agentName = logQ.data?.agent_name ?? "Anna";

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) =>
      [e.author_name, e.author_handle, e.message, e.replied_message, e.platform]
        .filter(Boolean)
        .some((v) => (v as string).toLowerCase().includes(q)),
    );
  }, [entries, search]);

  const aiCount = entries.filter((e) => e.used_ai).length;

  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!isAdmin)
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Lock className="h-4 w-4" /> Admins only.
      </p>
    );

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-2xl font-bold flex items-center gap-2">
          <ScrollText className="h-6 w-6 text-gold" /> Reply Log
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Every comment {agentName} (and your team) has replied to — who it was sent to, what was
          said, and when.
        </p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-border p-4">
          <div className="text-2xl font-bold">{entries.length}</div>
          <div className="text-xs text-muted-foreground">Total replies</div>
        </div>
        <div className="rounded-xl border border-border p-4">
          <div className="text-2xl font-bold">{aiCount}</div>
          <div className="text-xs text-muted-foreground">Sent as AI suggestion</div>
        </div>
        <div className="rounded-xl border border-border p-4">
          <div className="text-2xl font-bold">{entries.length - aiCount}</div>
          <div className="text-xs text-muted-foreground">Edited / manual</div>
        </div>
        <div className="rounded-xl border border-border p-4">
          <div className="text-sm font-semibold">{fmt(logQ.data?.last_reply_at ?? null)}</div>
          <div className="text-xs text-muted-foreground">Last reply</div>
        </div>
      </div>

      <Input
        placeholder="Search by person, handle, comment or reply…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="max-w-md"
      />

      {logQ.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading log…</p>
      ) : filtered.length === 0 ? (
        <p className="rounded-xl border border-border p-6 text-sm text-muted-foreground">
          No replies logged yet.
        </p>
      ) : (
        <div className="space-y-3">
          {filtered.map((e) => (
            <div key={e.id} className="rounded-xl border border-border p-4 space-y-3">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 font-semibold capitalize">
                  {e.platform ?? "social"}
                </span>
                <span className="inline-flex items-center gap-1 font-semibold text-foreground">
                  <User className="h-3.5 w-3.5 text-muted-foreground" />
                  {e.author_name || "Unknown"}
                  {e.author_handle ? (
                    <span className="font-normal text-muted-foreground">@{e.author_handle}</span>
                  ) : null}
                </span>
                <span className="ml-auto text-muted-foreground">{fmt(e.replied_at)}</span>
              </div>

              {/* Their comment */}
              <div className="rounded-lg bg-muted/50 px-3 py-2 text-sm">
                <div className="mb-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Their comment
                </div>
                <p className="whitespace-pre-wrap text-foreground">{e.message || "—"}</p>
              </div>

              {/* The reply */}
              <div className="rounded-lg border border-gold/30 bg-gold/5 px-3 py-2 text-sm">
                <div className="mb-0.5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Reply sent
                  <span className="inline-flex items-center gap-1 rounded-full bg-background px-1.5 py-0.5 text-[10px] normal-case text-foreground">
                    {e.replied_by_name === agentName ? (
                      <Bot className="h-3 w-3 text-gold" />
                    ) : (
                      <User className="h-3 w-3" />
                    )}
                    {e.replied_by_name}
                  </span>
                  {e.used_ai && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] normal-case text-emerald-700 dark:text-emerald-400">
                      <Sparkles className="h-3 w-3" /> AI suggestion
                    </span>
                  )}
                  {e.edited && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] normal-case text-amber-700 dark:text-amber-400">
                      <Pencil className="h-3 w-3" /> Edited
                    </span>
                  )}
                </div>
                <p className="whitespace-pre-wrap text-foreground">{e.replied_message || "—"}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
